<template>
  <div class="admin-container">

    <!-- Top Nav -->
    <nav class="admin-nav">
      <div class="nav-brand">
        <div class="brand-mark">EMS</div>
        <div class="brand-text">
          <div class="brand-title">Dashboard</div>
          <div class="brand-email">{{ user ? user.email : '' }}</div>
        </div>
      </div>
      <div class="nav-right">
        <button @click="switchToReader" class="nav-btn nav-btn--ghost">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
            <circle cx="12" cy="12" r="3"></circle>
          </svg>
          View Site
        </button>
        <button @click="handleLogout" class="nav-btn nav-btn--logout">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
            <polyline points="16 17 21 12 16 7"></polyline>
            <line x1="21" y1="12" x2="9" y2="12"></line>
          </svg>
          Logout
        </button>
      </div>
    </nav>

    <div class="admin-body">

      <!-- Sidebar -->
      <aside class="admin-sidebar">
        <div class="sidebar-section-label">Content</div>
        <button @click="activeTab = 'create'" :class="['sidebar-btn', { active: activeTab === 'create' }]">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          New Post
          <span v-if="editingPostId" class="sidebar-badge editing">Editing</span>
        </button>
        <button @click="activeTab = 'manage'" :class="['sidebar-btn', { active: activeTab === 'manage' }]">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
          </svg>
          Published
          <span v-if="publishedPosts.length" class="sidebar-count">{{ publishedPosts.length }}</span>
        </button>
        <button @click="activeTab = 'drafts'" :class="['sidebar-btn', { active: activeTab === 'drafts' }]">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
          </svg>
          Drafts
          <span v-if="draftPosts.length" class="sidebar-count sidebar-count--amber">{{ draftPosts.length }}</span>
        </button>

        <div class="sidebar-section-label" style="margin-top:24px;">Site</div>
        <button @click="activeTab = 'quotes'" :class="['sidebar-btn', { active: activeTab === 'quotes' }]">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z"></path>
            <path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z"></path>
          </svg>
          Quotes
          <span v-if="quotes.length" class="sidebar-count">{{ quotes.length }}</span>
        </button>

        <div class="sidebar-section-label" style="margin-top:24px;">Scripture</div>
        <button @click="activeTab = 'scripture_create'" :class="['sidebar-btn', { active: activeTab === 'scripture_create' }]">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
            <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
          </svg>
          New Scripture Post
          <span v-if="editingScripturePostId" class="sidebar-badge editing">Editing</span>
        </button>
        <button @click="activeTab = 'scripture_manage'" :class="['sidebar-btn', { active: activeTab === 'scripture_manage' }]">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
          </svg>
          Scripture Posts
          <span v-if="publishedScripturePosts.length" class="sidebar-count sidebar-count--indigo">{{ publishedScripturePosts.length }}</span>
        </button>
        <button @click="activeTab = 'scripture_drafts'" :class="['sidebar-btn', { active: activeTab === 'scripture_drafts' }]">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
          </svg>
          Scripture Drafts
          <span v-if="draftScripturePosts.length" class="sidebar-count sidebar-count--amber">{{ draftScripturePosts.length }}</span>
        </button>

        <!-- Quick stats -->
        <div class="sidebar-stats">
          <div class="ss-item">
            <div class="ss-num">{{ publishedPosts.length }}</div>
            <div class="ss-label">Posts</div>
          </div>
          <div class="ss-item">
            <div class="ss-num">{{ publishedScripturePosts.length }}</div>
            <div class="ss-label">Scripture</div>
          </div>
          <div class="ss-item">
            <div class="ss-num">{{ quotes.length }}</div>
            <div class="ss-label">Quotes</div>
          </div>
        </div>
      </aside>

      <!-- Main Content -->
      <main class="admin-main">

        <!-- ── CREATE / EDIT POST ── -->
        <div v-if="activeTab === 'create'" class="panel">
          <div class="panel-header">
            <div>
              <div class="panel-eyebrow">{{ editingPostId ? 'Editing Post' : 'New Post' }}</div>
              <h2 class="panel-title">{{ editingPostId ? 'Update Post' : 'Create New Post' }}</h2>
            </div>
            <button v-if="editingPostId" @click="resetForm" class="cancel-edit-btn">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
              Cancel Edit
            </button>
          </div>

          <div class="form-card">
            <!-- Title -->
            <div class="form-group">
              <label class="form-label">Title</label>
              <input
                type="text"
                v-model="newPost.title"
                placeholder="Enter a captivating title..."
                class="input-title"
              />
            </div>

            <div class="form-row-2">
              <div class="form-group">
                <label class="form-label">Category</label>
                <input type="text" v-model="newPost.category" placeholder="e.g. Philosophy, Research" class="input-base" />
              </div>
              <div class="form-group">
                <label class="form-label">Tags <span class="form-hint">comma separated</span></label>
                <input type="text" v-model="newPost.tags" placeholder="wisdom, philosophy, power" class="input-base" />
              </div>
            </div>

            <!-- Featured Image -->
            <div class="form-group">
              <label class="form-label">Featured Image</label>
              <div class="image-upload-area" :class="{ 'has-image': newPost.imageUrl }">
                <input type="file" @change="handleImageUpload($event, 'post')" accept="image/*" ref="imageUpload" style="display:none;" />
                <div v-if="!newPost.imageUrl" class="upload-empty" @click="$refs.imageUpload.click()">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                    <polyline points="21 15 16 10 5 21"></polyline>
                  </svg>
                  <p>Click to upload featured image</p>
                  <span>PNG, JPG, WebP</span>
                </div>
                <div v-else class="upload-preview">
                  <img :src="newPost.imageUrl" alt="Preview" />
                  <button @click="removeImage('post')" class="remove-img-btn">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                    Remove
                  </button>
                </div>
              </div>
            </div>

            <!-- Excerpt -->
            <div class="form-group">
              <label class="form-label">Excerpt <span class="form-hint">shown in blog preview</span></label>
              <textarea v-model="newPost.excerpt" placeholder="Brief summary of your post..." class="input-base textarea-sm" rows="3"></textarea>
            </div>

            <!-- Content Editor -->
            <div class="form-group">
              <label class="form-label">Content <span class="form-hint">Markdown supported</span></label>
              <div class="editor-wrap">
                <div class="editor-toolbar">
                  <button @click.prevent="formatText('bold', 'post')" class="tb-btn" title="Bold">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"></path><path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"></path></svg>
                  </button>
                  <button @click.prevent="formatText('italic', 'post')" class="tb-btn" title="Italic">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="19" y1="4" x2="10" y2="4"></line><line x1="14" y1="20" x2="5" y2="20"></line><line x1="15" y1="4" x2="9" y2="20"></line></svg>
                  </button>
                  <div class="tb-sep"></div>
                  <button @click.prevent="formatText('h2', 'post')" class="tb-btn tb-text">H2</button>
                  <button @click.prevent="formatText('h3', 'post')" class="tb-btn tb-text">H3</button>
                  <div class="tb-sep"></div>
                  <button @click.prevent="formatText('quote', 'post')" class="tb-btn" title="Blockquote">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z"></path></svg>
                  </button>
                </div>
                <textarea
                  ref="contentEditor"
                  v-model="newPost.content"
                  placeholder="Write your post content here..."
                  class="content-editor"
                  rows="18"
                ></textarea>
              </div>
            </div>

            <!-- Actions -->
            <div class="form-actions">
              <button @click="savePost(false)" class="action-btn action-btn--publish" :disabled="saveLoading">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 11 12 14 22 4"></polyline><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path></svg>
                {{ saveLoading ? 'Saving...' : (editingPostId ? 'Update Post' : 'Publish Post') }}
              </button>
              <button @click="savePost(true)" class="action-btn action-btn--draft" :disabled="saveLoading">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>
                Save Draft
              </button>
              <button @click="resetForm" class="action-btn action-btn--ghost">Clear</button>
            </div>
          </div>
        </div>

        <!-- ── PUBLISHED POSTS ── -->
        <div v-if="activeTab === 'manage'" class="panel">
          <div class="panel-header">
            <div>
              <div class="panel-eyebrow">Content</div>
              <h2 class="panel-title">Published Posts</h2>
            </div>
            <button @click="activeTab = 'create'" class="header-action-btn">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
              New Post
            </button>
          </div>

          <div v-if="postsLoading" class="state-loading">
            <div class="spinner"></div><p>Loading posts...</p>
          </div>
          <div v-else-if="publishedPosts.length === 0" class="state-empty">
            <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>
            <h3>No published posts yet</h3>
            <p>Create your first post to get started</p>
          </div>
          <div v-else class="posts-grid">
            <div v-for="post in publishedPosts" :key="post.id" class="post-card">
              <div v-if="post.imageUrl" class="pc-image">
                <img :src="post.imageUrl" :alt="post.title" />
              </div>
              <div class="pc-body">
                <div class="pc-meta">
                  <span class="pc-date">{{ formatDate(post.createdAt) }}</span>
                  <span v-if="post.category" class="pc-category">{{ post.category }}</span>
                </div>
                <h3 class="pc-title">{{ post.title }}</h3>
                <p class="pc-excerpt">{{ post.excerpt || 'No excerpt' }}</p>
                <div class="pc-actions">
                  <button @click="editPost(post)" class="pc-btn pc-btn--edit">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                    Edit
                  </button>
                  <button @click="deletePost(post.id)" class="pc-btn pc-btn--delete">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ── DRAFTS ── -->
        <div v-if="activeTab === 'drafts'" class="panel">
          <div class="panel-header">
            <div>
              <div class="panel-eyebrow">Content</div>
              <h2 class="panel-title">Draft Posts</h2>
            </div>
          </div>

          <div v-if="postsLoading" class="state-loading">
            <div class="spinner"></div><p>Loading drafts...</p>
          </div>
          <div v-else-if="draftPosts.length === 0" class="state-empty">
            <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>
            <h3>No drafts</h3>
            <p>Save posts as drafts to work on them later</p>
          </div>
          <div v-else class="posts-grid">
            <div v-for="post in draftPosts" :key="post.id" class="post-card">
              <div class="draft-ribbon">Draft</div>
              <div v-if="post.imageUrl" class="pc-image">
                <img :src="post.imageUrl" :alt="post.title" />
              </div>
              <div class="pc-body">
                <div class="pc-meta">
                  <span class="pc-date">{{ formatDate(post.createdAt) }}</span>
                  <span v-if="post.category" class="pc-category">{{ post.category }}</span>
                </div>
                <h3 class="pc-title">{{ post.title }}</h3>
                <p class="pc-excerpt">{{ post.excerpt || 'No excerpt' }}</p>
                <div class="pc-actions">
                  <button @click="editPost(post)" class="pc-btn pc-btn--edit">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                    Edit
                  </button>
                  <button @click="publishDraft(post.id)" class="pc-btn pc-btn--publish">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 11 12 14 22 4"></polyline><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path></svg>
                    Publish
                  </button>
                  <button @click="deletePost(post.id)" class="pc-btn pc-btn--delete">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ── QUOTES ── -->
        <div v-if="activeTab === 'quotes'" class="panel">
          <div class="panel-header">
            <div>
              <div class="panel-eyebrow">Site Content</div>
              <h2 class="panel-title">Manage Quotes</h2>
            </div>
          </div>

          <div class="form-card" style="margin-bottom:40px;">
            <div class="panel-sub-title">{{ editingQuoteId ? 'Edit Quote' : 'Add New Quote' }}</div>
            <div class="form-group">
              <label class="form-label">Quote Text</label>
              <textarea v-model="newQuote.text" placeholder="Enter the quote..." class="input-base textarea-sm" rows="3"></textarea>
            </div>
            <div class="form-group">
              <label class="form-label">Author</label>
              <input type="text" v-model="newQuote.author" placeholder="Author name" class="input-base" />
            </div>
            <div class="form-actions">
              <button @click="saveQuote" class="action-btn action-btn--publish" :disabled="quoteLoading">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>
                {{ editingQuoteId ? 'Update Quote' : 'Add Quote' }}
              </button>
              <button @click="resetQuoteForm" class="action-btn action-btn--ghost">Clear</button>
            </div>
          </div>

          <div v-if="quotesLoading" class="state-loading">
            <div class="spinner"></div><p>Loading quotes...</p>
          </div>
          <div v-else-if="quotes.length === 0" class="state-empty">
            <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1"><path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z"></path></svg>
            <h3>No quotes yet</h3>
            <p>Add inspiring quotes to display on your site</p>
          </div>
          <div v-else class="quotes-grid">
            <div v-for="quote in quotes" :key="quote.id" class="quote-card">
              <div class="qc-mark">"</div>
              <p class="qc-text">{{ quote.text }}</p>
              <p class="qc-author">— {{ quote.author }}</p>
              <div class="qc-actions">
                <button @click="editQuote(quote)" class="pc-btn pc-btn--edit">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                  Edit
                </button>
                <button @click="deleteQuote(quote.id)" class="pc-btn pc-btn--delete">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- ── SCRIPTURE CREATE / EDIT ── -->
        <div v-if="activeTab === 'scripture_create'" class="panel">
          <div class="panel-header">
            <div>
              <div class="panel-eyebrow scripture-eyebrow">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                  <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
                </svg>
                Scripture
              </div>
              <h2 class="panel-title">{{ editingScripturePostId ? 'Edit Scripture Post' : 'New Scripture Post' }}</h2>
            </div>
            <button v-if="editingScripturePostId" @click="resetScriptureForm" class="cancel-edit-btn">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
              Cancel Edit
            </button>
          </div>

          <div class="form-card form-card--scripture">
            <!-- Title -->
            <div class="form-group">
              <label class="form-label">Title</label>
              <input
                type="text"
                v-model="newScripturePost.title"
                placeholder="e.g. Ezekiel's Wheels and Non-Euclidean Space..."
                class="input-title"
              />
            </div>

            <div class="form-row-2">
              <div class="form-group">
                <label class="form-label">Category</label>
                <input type="text" v-model="newScripturePost.category" placeholder="e.g. Cosmology, Prophecy, Enoch" class="input-base" />
              </div>
              <div class="form-group">
                <label class="form-label">Tags <span class="form-hint">comma separated</span></label>
                <input type="text" v-model="newScripturePost.tags" placeholder="yahweh, dimensions, enoch, kjv" class="input-base" />
              </div>
            </div>

            <!-- Featured Image -->
            <div class="form-group">
              <label class="form-label">Featured Image</label>
              <div class="image-upload-area" :class="{ 'has-image': newScripturePost.imageUrl }">
                <input type="file" @change="handleImageUpload($event, 'scripture')" accept="image/*" ref="scriptureImageUpload" style="display:none;" />
                <div v-if="!newScripturePost.imageUrl" class="upload-empty" @click="$refs.scriptureImageUpload.click()">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                    <polyline points="21 15 16 10 5 21"></polyline>
                  </svg>
                  <p>Click to upload featured image</p>
                  <span>PNG, JPG, WebP</span>
                </div>
                <div v-else class="upload-preview">
                  <img :src="newScripturePost.imageUrl" alt="Preview" />
                  <button @click="removeImage('scripture')" class="remove-img-btn">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                    Remove
                  </button>
                </div>
              </div>
            </div>

            <!-- Excerpt -->
            <div class="form-group">
              <label class="form-label">Excerpt <span class="form-hint">shown in scripture page preview</span></label>
              <textarea v-model="newScripturePost.excerpt" placeholder="Brief summary of your scriptural study..." class="input-base textarea-sm" rows="3"></textarea>
            </div>

            <!-- Content Editor -->
            <div class="form-group">
              <label class="form-label">Content <span class="form-hint">Markdown supported</span></label>
              <div class="editor-wrap editor-wrap--scripture">
                <div class="editor-toolbar">
                  <button @click.prevent="formatText('bold', 'scripture')" class="tb-btn" title="Bold">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"></path><path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"></path></svg>
                  </button>
                  <button @click.prevent="formatText('italic', 'scripture')" class="tb-btn" title="Italic">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="19" y1="4" x2="10" y2="4"></line><line x1="14" y1="20" x2="5" y2="20"></line><line x1="15" y1="4" x2="9" y2="20"></line></svg>
                  </button>
                  <div class="tb-sep"></div>
                  <button @click.prevent="formatText('h2', 'scripture')" class="tb-btn tb-text">H2</button>
                  <button @click.prevent="formatText('h3', 'scripture')" class="tb-btn tb-text">H3</button>
                  <div class="tb-sep"></div>
                  <button @click.prevent="formatText('quote', 'scripture')" class="tb-btn" title="Blockquote">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z"></path></svg>
                  </button>
                </div>
                <textarea
                  ref="scriptureContentEditor"
                  v-model="newScripturePost.content"
                  placeholder="Write your scriptural study here. Reference KJV passages, Enoch parallels, and dimensional interpretations..."
                  class="content-editor"
                  rows="18"
                ></textarea>
              </div>
            </div>

            <!-- Actions -->
            <div class="form-actions">
              <button @click="saveScripturePost(false)" class="action-btn action-btn--scripture" :disabled="scriptureSaveLoading">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 11 12 14 22 4"></polyline><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path></svg>
                {{ scriptureSaveLoading ? 'Saving...' : (editingScripturePostId ? 'Update Post' : 'Publish to Scripture') }}
              </button>
              <button @click="saveScripturePost(true)" class="action-btn action-btn--draft" :disabled="scriptureSaveLoading">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>
                Save Draft
              </button>
              <button @click="resetScriptureForm" class="action-btn action-btn--ghost">Clear</button>
            </div>
          </div>
        </div>

        <!-- ── SCRIPTURE PUBLISHED ── -->
        <div v-if="activeTab === 'scripture_manage'" class="panel">
          <div class="panel-header">
            <div>
              <div class="panel-eyebrow scripture-eyebrow">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                  <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
                </svg>
                Scripture
              </div>
              <h2 class="panel-title">Published Scripture Posts</h2>
            </div>
            <button @click="activeTab = 'scripture_create'" class="header-action-btn header-action-btn--scripture">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
              New Scripture Post
            </button>
          </div>

          <div v-if="scriptureLoading" class="state-loading">
            <div class="spinner spinner--scripture"></div><p>Loading scripture posts...</p>
          </div>
          <div v-else-if="publishedScripturePosts.length === 0" class="state-empty">
            <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
              <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
              <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
            </svg>
            <h3>No scripture posts yet</h3>
            <p>Create your first scriptural study post</p>
          </div>
          <div v-else class="posts-grid">
            <div v-for="post in publishedScripturePosts" :key="post.id" class="post-card post-card--scripture">
              <div v-if="post.imageUrl" class="pc-image">
                <img :src="post.imageUrl" :alt="post.title" />
              </div>
              <div class="pc-body">
                <div class="pc-meta">
                  <span class="pc-date">{{ formatDate(post.createdAt) }}</span>
                  <span v-if="post.category" class="pc-category pc-category--scripture">{{ post.category }}</span>
                </div>
                <h3 class="pc-title">{{ post.title }}</h3>
                <p class="pc-excerpt">{{ post.excerpt || 'No excerpt' }}</p>
                <div class="pc-actions">
                  <button @click="editScripturePost(post)" class="pc-btn pc-btn--edit">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                    Edit
                  </button>
                  <button @click="deleteScripturePost(post.id)" class="pc-btn pc-btn--delete">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ── SCRIPTURE DRAFTS ── -->
        <div v-if="activeTab === 'scripture_drafts'" class="panel">
          <div class="panel-header">
            <div>
              <div class="panel-eyebrow scripture-eyebrow">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                  <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
                </svg>
                Scripture
              </div>
              <h2 class="panel-title">Scripture Drafts</h2>
            </div>
          </div>

          <div v-if="scriptureLoading" class="state-loading">
            <div class="spinner spinner--scripture"></div><p>Loading drafts...</p>
          </div>
          <div v-else-if="draftScripturePosts.length === 0" class="state-empty">
            <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
              <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
              <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
            </svg>
            <h3>No scripture drafts</h3>
            <p>Save scripture posts as drafts to finish them later</p>
          </div>
          <div v-else class="posts-grid">
            <div v-for="post in draftScripturePosts" :key="post.id" class="post-card post-card--scripture">
              <div class="draft-ribbon draft-ribbon--scripture">Draft</div>
              <div v-if="post.imageUrl" class="pc-image">
                <img :src="post.imageUrl" :alt="post.title" />
              </div>
              <div class="pc-body">
                <div class="pc-meta">
                  <span class="pc-date">{{ formatDate(post.createdAt) }}</span>
                  <span v-if="post.category" class="pc-category pc-category--scripture">{{ post.category }}</span>
                </div>
                <h3 class="pc-title">{{ post.title }}</h3>
                <p class="pc-excerpt">{{ post.excerpt || 'No excerpt' }}</p>
                <div class="pc-actions">
                  <button @click="editScripturePost(post)" class="pc-btn pc-btn--edit">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                    Edit
                  </button>
                  <button @click="publishScriptureDraft(post.id)" class="pc-btn pc-btn--scripture-pub">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 11 12 14 22 4"></polyline><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path></svg>
                    Publish
                  </button>
                  <button @click="deleteScripturePost(post.id)" class="pc-btn pc-btn--delete">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

      </main>
    </div>
  </div>
</template>

<script>
import { db, storage, auth } from '../firebase'
import firebase from '../firebase'

export default {
  name: 'AdminDashboard',
  data () {
    return {
      user: null,
      activeTab: 'create',

      // ── Blog posts ──────────────────────────────────────────
      newPost: { title: '', content: '', excerpt: '', category: '', tags: '', imageUrl: '', imageFile: null },
      posts: [],
      postsLoading: false,
      saveLoading: false,
      editingPostId: null,

      // ── Quotes ─────────────────────────────────────────────
      newQuote: { text: '', author: '' },
      quotes: [],
      quotesLoading: false,
      quoteLoading: false,
      editingQuoteId: null,

      // ── Scripture posts ─────────────────────────────────────
      newScripturePost: { title: '', content: '', excerpt: '', category: '', tags: '', imageUrl: '', imageFile: null },
      scripturePosts: [],
      scriptureLoading: false,
      scriptureSaveLoading: false,
      editingScripturePostId: null,
    }
  },
  computed: {
    publishedPosts ()         { return this.posts.filter(p => p.status !== 'draft') },
    draftPosts ()             { return this.posts.filter(p => p.status === 'draft') },
    publishedScripturePosts () { return this.scripturePosts.filter(p => p.status !== 'draft') },
    draftScripturePosts ()    { return this.scripturePosts.filter(p => p.status === 'draft') },
  },
  mounted () {
    auth.onAuthStateChanged(user => {
      this.user = user
      if (!user) this.$router.push('/login')
    })
    this.loadPosts()
    this.loadQuotes()
    this.loadScripturePosts()
  },
  methods: {

    // ── Auth ──────────────────────────────────────────────────
    async handleLogout () {
      try { await auth.signOut(); this.$router.push('/') } catch (e) { console.error(e) }
    },
    switchToReader () { this.$router.push('/') },

    // ── Blog Posts ────────────────────────────────────────────
    async loadPosts () {
      this.postsLoading = true
      try {
        const snap = await db.collection('posts').orderBy('createdAt', 'desc').get()
        this.posts = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      } catch (e) { console.error(e) } finally { this.postsLoading = false }
    },

    async savePost (isDraft = false) {
      if (!this.newPost.title || !this.newPost.content) { alert('Title and content are required'); return }
      this.saveLoading = true
      try {
        let imageUrl = this.newPost.imageUrl
        if (this.newPost.imageFile) {
          const ref = storage.ref(`blog-images/${Date.now()}_${this.newPost.imageFile.name}`)
          const task = await ref.put(this.newPost.imageFile)
          imageUrl = await task.ref.getDownloadURL()
        }
        const data = {
          title:     this.newPost.title,
          content:   this.newPost.content,
          excerpt:   this.newPost.excerpt,
          category:  this.newPost.category,
          tags:      this.newPost.tags ? this.newPost.tags.split(',').map(t => t.trim()) : [],
          imageUrl,
          status:    isDraft ? 'draft' : 'published',
          updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        }
        if (this.editingPostId) {
          await db.collection('posts').doc(this.editingPostId).update(data)
          this.editingPostId = null
        } else {
          data.createdAt = firebase.firestore.FieldValue.serverTimestamp()
          data.author    = this.user.email
          await db.collection('posts').add(data)
        }
        this.resetForm()
        this.activeTab = isDraft ? 'drafts' : 'manage'
        await this.loadPosts()
      } catch (e) { console.error(e); alert('Error saving: ' + e.message) } finally { this.saveLoading = false }
    },

    async publishDraft (id) {
      try {
        await db.collection('posts').doc(id).update({ status: 'published', updatedAt: firebase.firestore.FieldValue.serverTimestamp() })
        await this.loadPosts(); this.activeTab = 'manage'
      } catch (e) { alert('Error: ' + e.message) }
    },

    async deletePost (id) {
      if (!confirm('Delete this post?')) return
      try { await db.collection('posts').doc(id).delete(); await this.loadPosts() } catch (e) { alert('Error: ' + e.message) }
    },

    editPost (post) {
      this.newPost = {
        title:    post.title,
        content:  post.content,
        excerpt:  post.excerpt || '',
        category: post.category || '',
        tags:     Array.isArray(post.tags) ? post.tags.join(', ') : '',
        imageUrl: post.imageUrl || '',
        imageFile: null,
      }
      this.editingPostId = post.id
      this.activeTab = 'create'
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },

    resetForm () {
      this.newPost = { title: '', content: '', excerpt: '', category: '', tags: '', imageUrl: '', imageFile: null }
      this.editingPostId = null
      if (this.$refs.imageUpload) this.$refs.imageUpload.value = ''
    },

    // ── Quotes ────────────────────────────────────────────────
    async loadQuotes () {
      this.quotesLoading = true
      try {
        const snap = await db.collection('quotes').orderBy('createdAt', 'desc').get()
        this.quotes = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      } catch (e) { console.error(e) } finally { this.quotesLoading = false }
    },

    async saveQuote () {
      if (!this.newQuote.text || !this.newQuote.author) { alert('Quote and author required'); return }
      this.quoteLoading = true
      try {
        const data = { text: this.newQuote.text, author: this.newQuote.author, updatedAt: firebase.firestore.FieldValue.serverTimestamp() }
        if (this.editingQuoteId) {
          await db.collection('quotes').doc(this.editingQuoteId).update(data)
          this.editingQuoteId = null
        } else {
          data.createdAt = firebase.firestore.FieldValue.serverTimestamp()
          await db.collection('quotes').add(data)
        }
        this.resetQuoteForm(); await this.loadQuotes()
      } catch (e) { alert('Error: ' + e.message) } finally { this.quoteLoading = false }
    },

    async deleteQuote (id) {
      if (!confirm('Delete this quote?')) return
      try { await db.collection('quotes').doc(id).delete(); await this.loadQuotes() } catch (e) { alert('Error: ' + e.message) }
    },

    editQuote (quote) {
      this.newQuote = { text: quote.text, author: quote.author }
      this.editingQuoteId = quote.id
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },

    resetQuoteForm () { this.newQuote = { text: '', author: '' }; this.editingQuoteId = null },

    // ── Scripture Posts ───────────────────────────────────────
    async loadScripturePosts () {
      this.scriptureLoading = true
      try {
        const snap = await db.collection('scripture_posts').orderBy('createdAt', 'desc').get()
        this.scripturePosts = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      } catch (e) { console.error(e) } finally { this.scriptureLoading = false }
    },

    async saveScripturePost (isDraft = false) {
      if (!this.newScripturePost.title || !this.newScripturePost.content) { alert('Title and content are required'); return }
      this.scriptureSaveLoading = true
      try {
        let imageUrl = this.newScripturePost.imageUrl
        if (this.newScripturePost.imageFile) {
          const ref = storage.ref(`scripture-images/${Date.now()}_${this.newScripturePost.imageFile.name}`)
          const task = await ref.put(this.newScripturePost.imageFile)
          imageUrl = await task.ref.getDownloadURL()
        }
        const data = {
          title:     this.newScripturePost.title,
          content:   this.newScripturePost.content,
          excerpt:   this.newScripturePost.excerpt,
          category:  this.newScripturePost.category,
          tags:      this.newScripturePost.tags ? this.newScripturePost.tags.split(',').map(t => t.trim()) : [],
          imageUrl,
          status:    isDraft ? 'draft' : 'published',
          updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        }
        if (this.editingScripturePostId) {
          await db.collection('scripture_posts').doc(this.editingScripturePostId).update(data)
          this.editingScripturePostId = null
        } else {
          data.createdAt = firebase.firestore.FieldValue.serverTimestamp()
          data.author    = this.user.email
          await db.collection('scripture_posts').add(data)
        }
        this.resetScriptureForm()
        this.activeTab = isDraft ? 'scripture_drafts' : 'scripture_manage'
        await this.loadScripturePosts()
      } catch (e) { console.error(e); alert('Error saving: ' + e.message) } finally { this.scriptureSaveLoading = false }
    },

    async publishScriptureDraft (id) {
      try {
        await db.collection('scripture_posts').doc(id).update({ status: 'published', updatedAt: firebase.firestore.FieldValue.serverTimestamp() })
        await this.loadScripturePosts(); this.activeTab = 'scripture_manage'
      } catch (e) { alert('Error: ' + e.message) }
    },

    async deleteScripturePost (id) {
      if (!confirm('Delete this scripture post?')) return
      try { await db.collection('scripture_posts').doc(id).delete(); await this.loadScripturePosts() } catch (e) { alert('Error: ' + e.message) }
    },

    editScripturePost (post) {
      this.newScripturePost = {
        title:    post.title,
        content:  post.content,
        excerpt:  post.excerpt || '',
        category: post.category || '',
        tags:     Array.isArray(post.tags) ? post.tags.join(', ') : '',
        imageUrl: post.imageUrl || '',
        imageFile: null,
      }
      this.editingScripturePostId = post.id
      this.activeTab = 'scripture_create'
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },

    resetScriptureForm () {
      this.newScripturePost = { title: '', content: '', excerpt: '', category: '', tags: '', imageUrl: '', imageFile: null }
      this.editingScripturePostId = null
      if (this.$refs.scriptureImageUpload) this.$refs.scriptureImageUpload.value = ''
    },

    // ── Shared helpers ────────────────────────────────────────
    handleImageUpload (event, target) {
      const file = event.target.files[0]
      if (!file) return
      const reader = new FileReader()
      if (target === 'scripture') {
        this.newScripturePost.imageFile = file
        reader.onload = e => { this.newScripturePost.imageUrl = e.target.result }
      } else {
        this.newPost.imageFile = file
        reader.onload = e => { this.newPost.imageUrl = e.target.result }
      }
      reader.readAsDataURL(file)
    },

    removeImage (target) {
      if (target === 'scripture') {
        this.newScripturePost.imageUrl = ''
        this.newScripturePost.imageFile = null
        if (this.$refs.scriptureImageUpload) this.$refs.scriptureImageUpload.value = ''
      } else {
        this.newPost.imageUrl = ''
        this.newPost.imageFile = null
        if (this.$refs.imageUpload) this.$refs.imageUpload.value = ''
      }
    },

    formatText (format, target) {
      const isScripture = target === 'scripture'
      const editor = isScripture ? this.$refs.scriptureContentEditor : this.$refs.contentEditor
      const model  = isScripture ? this.newScripturePost : this.newPost
      const start  = editor.selectionStart
      const end    = editor.selectionEnd
      const sel    = model.content.substring(start, end)
      const map    = { bold: `**${sel}**`, italic: `*${sel}*`, h2: `## ${sel}`, h3: `### ${sel}`, quote: `> ${sel}` }
      model.content = model.content.substring(0, start) + map[format] + model.content.substring(end)
    },

    formatDate (ts) {
      if (!ts) return 'Just now'
      const d = ts.toDate ? ts.toDate() : new Date(ts)
      return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    },
  },
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&family=Inter:wght@400;500;600;700&display=swap');

/* ─── Base ─────────────────────────────────────────────────── */
.admin-container { min-height: 100vh; background: #0d1117; color: #f5f0e8; font-family: 'Inter', sans-serif; }

/* ─── Nav ──────────────────────────────────────────────────── */
.admin-nav {
  height: 60px;
  background: #0b1628;
  border-bottom: 1px solid rgba(201,168,76,0.15);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  position: sticky;
  top: 0;
  z-index: 50;
}
.nav-brand { display: flex; align-items: center; gap: 12px; }
.brand-mark {
  width: 36px; height: 36px;
  background: #c9a84c; color: #0b1628;
  display: flex; align-items: center; justify-content: center;
  font-family: 'Playfair Display', serif; font-weight: 700; font-size: 13px; letter-spacing: 1px; flex-shrink: 0;
}
.brand-title  { font-size: 14px; font-weight: 700; color: #f5f0e8; letter-spacing: 0.02em; }
.brand-email  { font-size: 11px; color: #4b5563; font-weight: 400; }
.nav-right    { display: flex; gap: 8px; }
.nav-btn {
  display: flex; align-items: center; gap: 7px; padding: 7px 14px;
  font-family: 'Inter', sans-serif; font-size: 12px; font-weight: 600;
  letter-spacing: 0.04em; cursor: pointer; border: none; transition: all 0.2s;
}
.nav-btn--ghost  { background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.08); color: #718096; }
.nav-btn--ghost:hover { color: #c8c0b0; border-color: rgba(255,255,255,0.15); }
.nav-btn--logout { background: rgba(201,168,76,0.08); border: 1px solid rgba(201,168,76,0.2); color: #c9a84c; }
.nav-btn--logout:hover { background: rgba(201,168,76,0.15); }

/* ─── Layout ───────────────────────────────────────────────── */
.admin-body { display: flex; min-height: calc(100vh - 60px); }

/* ─── Sidebar ──────────────────────────────────────────────── */
.admin-sidebar {
  width: 220px; flex-shrink: 0;
  background: #0b1628;
  border-right: 1px solid rgba(201,168,76,0.1);
  padding: 24px 16px;
  display: flex; flex-direction: column; gap: 2px;
}
.sidebar-section-label {
  font-size: 9px; font-weight: 700; letter-spacing: 0.2em; text-transform: uppercase;
  color: #2d3748; padding: 0 10px; margin-bottom: 6px; margin-top: 4px;
}
.sidebar-btn {
  width: 100%; display: flex; align-items: center; gap: 10px; padding: 10px 12px;
  background: transparent; border: none; color: #4b5563;
  font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 500;
  cursor: pointer; transition: all 0.15s; text-align: left; border-radius: 0; position: relative;
}
.sidebar-btn:hover { background: rgba(201,168,76,0.05); color: #c8c0b0; }
.sidebar-btn.active { background: rgba(201,168,76,0.1); color: #c9a84c; border-left: 2px solid #c9a84c; }
.sidebar-btn.active svg { color: #c9a84c; }

/* Scripture sidebar active state gets an indigo tint instead */
.sidebar-btn[class*="scripture"].active,
.sidebar-btn.active.scripture-active {
  background: rgba(99,102,241,0.1);
  color: #818cf8;
  border-left: 2px solid #818cf8;
}

.sidebar-count, .sidebar-badge {
  margin-left: auto; font-size: 10px; font-weight: 700; padding: 2px 7px; border-radius: 10px;
}
.sidebar-count           { background: rgba(201,168,76,0.12); color: #c9a84c; }
.sidebar-count--amber    { background: rgba(245,158,11,0.12); color: #f59e0b; }
.sidebar-count--indigo   { background: rgba(99,102,241,0.12); color: #818cf8; }
.sidebar-badge           { background: rgba(201,168,76,0.12); color: #c9a84c; font-size: 9px; letter-spacing: 0.06em; text-transform: uppercase; }
.sidebar-badge.editing   { background: rgba(59,130,246,0.12); color: #60a5fa; }

.sidebar-stats {
  margin-top: auto; padding: 16px 10px 4px;
  border-top: 1px solid rgba(201,168,76,0.08);
  display: flex; gap: 0;
}
.ss-item  { flex: 1; text-align: center; }
.ss-num   { font-family: 'Playfair Display', serif; font-size: 20px; font-weight: 700; color: #c9a84c; line-height: 1; margin-bottom: 4px; }
.ss-label { font-size: 9px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; color: #2d3748; }

/* ─── Main ─────────────────────────────────────────────────── */
.admin-main { flex: 1; padding: 32px; overflow-y: auto; }

/* ─── Panel ────────────────────────────────────────────────── */
.panel { max-width: 900px; }
.panel-header {
  display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 28px;
}
.panel-eyebrow {
  font-size: 10px; font-weight: 700; letter-spacing: 0.2em; text-transform: uppercase;
  color: #c9a84c; margin-bottom: 6px;
}
.scripture-eyebrow {
  display: flex; align-items: center; gap: 7px;
  color: #818cf8;
}
.panel-title     { font-family: 'Playfair Display', serif; font-size: 28px; font-weight: 700; color: #f5f0e8; margin: 0; }
.panel-sub-title { font-family: 'Playfair Display', serif; font-size: 18px; font-weight: 700; color: #f5f0e8; margin-bottom: 20px; }

.header-action-btn {
  display: flex; align-items: center; gap: 7px; padding: 9px 16px;
  background: #c9a84c; color: #0b1628;
  border: none; font-size: 12px; font-weight: 700; letter-spacing: 0.06em; cursor: pointer; transition: background 0.2s;
}
.header-action-btn:hover { background: #e8cc80; }

.header-action-btn--scripture {
  background: #6366f1; color: #fff;
}
.header-action-btn--scripture:hover { background: #818cf8; }

.cancel-edit-btn {
  display: flex; align-items: center; gap: 6px; padding: 8px 14px;
  background: transparent; border: 1px solid rgba(255,255,255,0.1); color: #718096;
  font-size: 12px; font-weight: 600; cursor: pointer; transition: all 0.2s;
}
.cancel-edit-btn:hover { border-color: rgba(255,255,255,0.2); color: #c8c0b0; }

/* ─── Form Card ────────────────────────────────────────────── */
.form-card {
  background: #0f1a2e; border: 1px solid rgba(201,168,76,0.1);
  padding: 32px; margin-bottom: 8px;
}
.form-card--scripture {
  border-color: rgba(99,102,241,0.2);
}

.form-group { margin-bottom: 24px; }
.form-group:last-child { margin-bottom: 0; }
.form-label {
  display: block; font-size: 11px; font-weight: 700; letter-spacing: 0.1em;
  text-transform: uppercase; color: #718096; margin-bottom: 8px;
}
.form-hint { font-weight: 400; letter-spacing: 0; text-transform: none; color: #2d3748; margin-left: 6px; }
.form-row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 24px; }
@media (max-width: 600px) { .form-row-2 { grid-template-columns: 1fr; } }

.input-base, .input-title {
  width: 100%; padding: 12px 14px;
  background: #060e1a; border: 1px solid rgba(201,168,76,0.12);
  color: #f5f0e8; font-family: 'Inter', sans-serif; font-size: 14px;
  outline: none; transition: border-color 0.2s, box-shadow 0.2s; resize: vertical;
}
.input-base::placeholder, .input-title::placeholder { color: #2d3748; }
.input-base:focus, .input-title:focus {
  border-color: rgba(201,168,76,0.4); box-shadow: 0 0 0 2px rgba(201,168,76,0.06);
}
.input-title { font-family: 'Playfair Display', serif; font-size: 22px; padding: 14px 16px; }
.textarea-sm { min-height: 90px; }

/* Image Upload */
.image-upload-area {
  border: 1px dashed rgba(201,168,76,0.2); background: #060e1a;
  transition: border-color 0.2s, background 0.2s; min-height: 160px;
}
.image-upload-area:not(.has-image):hover { border-color: rgba(201,168,76,0.4); background: rgba(201,168,76,0.03); }
.upload-empty {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  min-height: 160px; gap: 10px; cursor: pointer; color: #2d3748; padding: 24px;
}
.upload-empty svg { color: #374151; }
.upload-empty p   { font-size: 13px; font-weight: 600; color: #4b5563; margin: 0; }
.upload-empty span { font-size: 11px; color: #2d3748; }
.upload-preview { position: relative; }
.upload-preview img { width: 100%; display: block; max-height: 280px; object-fit: cover; }
.remove-img-btn {
  position: absolute; top: 10px; right: 10px;
  display: flex; align-items: center; gap: 5px; padding: 6px 12px;
  background: rgba(0,0,0,0.75); border: none; color: #f5f0e8;
  font-size: 11px; font-weight: 700; cursor: pointer; transition: background 0.2s;
}
.remove-img-btn:hover { background: #dc2626; }

/* Editor */
.editor-wrap { border: 1px solid rgba(201,168,76,0.12); }
.editor-wrap--scripture { border-color: rgba(99,102,241,0.25); }

.editor-toolbar {
  display: flex; align-items: center; gap: 4px; padding: 10px 12px;
  background: #060e1a; border-bottom: 1px solid rgba(201,168,76,0.08);
}
.tb-btn {
  width: 32px; height: 32px; display: flex; align-items: center; justify-content: center;
  background: transparent; border: 1px solid transparent; color: #4b5563;
  cursor: pointer; transition: all 0.15s; font-size: 12px; font-weight: 700;
}
.tb-btn:hover { background: rgba(201,168,76,0.08); border-color: rgba(201,168,76,0.2); color: #c9a84c; }
.tb-text { font-family: 'Playfair Display', serif; font-size: 11px; width: auto; padding: 0 8px; }
.tb-sep  { width: 1px; height: 20px; background: rgba(255,255,255,0.07); margin: 0 4px; }

.content-editor {
  width: 100%; padding: 20px;
  background: #060e1a; border: none;
  color: #c8c0b0; font-family: 'Inter', sans-serif; font-size: 14px; line-height: 1.75;
  resize: vertical; min-height: 380px; outline: none;
}
.content-editor::placeholder { color: #1f2937; }
.content-editor:focus { box-shadow: 0 0 0 2px rgba(201,168,76,0.06) inset; }

/* Form actions */
.form-actions { display: flex; gap: 10px; margin-top: 28px; flex-wrap: wrap; }
.action-btn {
  display: flex; align-items: center; gap: 8px; padding: 12px 22px; border: none;
  font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 700;
  letter-spacing: 0.06em; text-transform: uppercase; cursor: pointer; transition: all 0.2s; white-space: nowrap;
}
.action-btn--publish  { background: #c9a84c; color: #0b1628; }
.action-btn--publish:hover:not(:disabled) { background: #e8cc80; }
.action-btn--publish:disabled { opacity: 0.6; cursor: not-allowed; }

.action-btn--scripture { background: #6366f1; color: #fff; }
.action-btn--scripture:hover:not(:disabled) { background: #818cf8; }
.action-btn--scripture:disabled { opacity: 0.6; cursor: not-allowed; }

.action-btn--draft { background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1); color: #9ca3af; }
.action-btn--draft:hover:not(:disabled) { background: rgba(255,255,255,0.1); color: #c8c0b0; }
.action-btn--draft:disabled { opacity: 0.5; cursor: not-allowed; }

.action-btn--ghost { background: transparent; border: 1px solid rgba(255,255,255,0.08); color: #4b5563; }
.action-btn--ghost:hover { color: #718096; border-color: rgba(255,255,255,0.15); }

/* ─── States ───────────────────────────────────────────────── */
.state-loading, .state-empty { text-align: center; padding: 80px 20px; }
.spinner {
  width: 36px; height: 36px; margin: 0 auto 16px;
  border: 3px solid rgba(201,168,76,0.1); border-top-color: #c9a84c;
  border-radius: 50%; animation: spin 0.8s linear infinite;
}
.spinner--scripture { border-color: rgba(99,102,241,0.1); border-top-color: #818cf8; }
@keyframes spin { to { transform: rotate(360deg); } }
.state-loading p, .state-empty p { font-size: 14px; color: #4b5563; margin-top: 8px; }
.state-empty svg { margin: 0 auto 20px; color: #1f2937; display: block; }
.state-empty h3 { font-family: 'Playfair Display', serif; font-size: 22px; color: #c8c0b0; margin-bottom: 8px; }

/* ─── Post Grid ────────────────────────────────────────────── */
.posts-grid {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 2px;
}
.post-card {
  background: #0f1a2e; border: 1px solid rgba(201,168,76,0.08);
  overflow: hidden; position: relative; transition: border-color 0.2s;
}
.post-card:hover { border-color: rgba(201,168,76,0.25); }
.post-card--scripture { border-color: rgba(99,102,241,0.1); }
.post-card--scripture:hover { border-color: rgba(99,102,241,0.35); }

.draft-ribbon {
  position: absolute; top: 12px; right: 12px; z-index: 2;
  background: #f59e0b; color: #0b1628;
  font-size: 9px; font-weight: 800; letter-spacing: 0.1em; text-transform: uppercase; padding: 4px 10px;
}
.draft-ribbon--scripture { background: #6366f1; color: #fff; }

.pc-image { width: 100%; height: 160px; overflow: hidden; background: #060e1a; }
.pc-image img { width: 100%; height: 100%; object-fit: cover; }
.pc-body { padding: 20px; }
.pc-meta { display: flex; gap: 8px; margin-bottom: 10px; align-items: center; flex-wrap: wrap; }
.pc-date { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em; color: #2d3748; }
.pc-category {
  font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em;
  color: #c9a84c; background: rgba(201,168,76,0.1); border: 1px solid rgba(201,168,76,0.15); padding: 2px 8px;
}
.pc-category--scripture {
  color: #818cf8; background: rgba(99,102,241,0.1); border-color: rgba(99,102,241,0.2);
}
.pc-title  { font-family: 'Playfair Display', serif; font-size: 16px; font-weight: 700; color: #f5f0e8; margin-bottom: 8px; line-height: 1.3; }
.pc-excerpt { font-size: 12px; line-height: 1.6; color: #4b5563; margin-bottom: 16px; }
.pc-actions {
  display: flex; gap: 6px; padding-top: 14px; border-top: 1px solid rgba(201,168,76,0.06);
}
.pc-btn {
  flex: 1; display: flex; align-items: center; justify-content: center; gap: 5px;
  padding: 8px 10px; border: 1px solid transparent;
  font-size: 11px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase;
  cursor: pointer; transition: all 0.15s; background: transparent;
}
.pc-btn--edit    { color: #718096; border-color: rgba(255,255,255,0.07); }
.pc-btn--edit:hover { background: rgba(201,168,76,0.08); border-color: rgba(201,168,76,0.2); color: #c9a84c; }
.pc-btn--publish { color: #34d399; border-color: rgba(52,211,153,0.15); }
.pc-btn--publish:hover { background: rgba(52,211,153,0.08); border-color: rgba(52,211,153,0.3); }
.pc-btn--scripture-pub { color: #818cf8; border-color: rgba(99,102,241,0.2); }
.pc-btn--scripture-pub:hover { background: rgba(99,102,241,0.1); border-color: rgba(99,102,241,0.4); }
.pc-btn--delete  { color: #4b5563; border-color: rgba(255,255,255,0.05); }
.pc-btn--delete:hover { background: rgba(239,68,68,0.08); border-color: rgba(239,68,68,0.2); color: #f87171; }

/* ─── Quotes Grid ──────────────────────────────────────────── */
.quotes-grid {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 2px;
}
.quote-card {
  background: #0f1a2e; border: 1px solid rgba(201,168,76,0.08); border-left: 3px solid rgba(201,168,76,0.4);
  padding: 24px; transition: border-color 0.2s;
}
.quote-card:hover { border-color: rgba(201,168,76,0.25); }
.qc-mark   { font-family: 'Playfair Display', serif; font-size: 36px; color: #c9a84c; opacity: 0.4; line-height: 1; margin-bottom: 10px; }
.qc-text   { font-family: 'Playfair Display', serif; font-size: 15px; font-style: italic; line-height: 1.6; color: #c8c0b0; margin-bottom: 10px; }
.qc-author { font-size: 12px; font-weight: 600; color: #8a6e2a; letter-spacing: 0.06em; margin-bottom: 16px; }
.qc-actions { display: flex; gap: 6px; padding-top: 14px; border-top: 1px solid rgba(201,168,76,0.06); }

/* ─── Responsive ─────────────────────────────────────────────── */
@media (max-width: 768px) {
  .admin-body { flex-direction: column; }
  .admin-sidebar {
    width: 100%; border-right: none; border-bottom: 1px solid rgba(201,168,76,0.1);
    flex-direction: row; flex-wrap: wrap; padding: 12px; gap: 4px;
  }
  .sidebar-section-label { display: none; }
  .sidebar-btn { width: auto; flex: 1; justify-content: center; padding: 10px 8px; font-size: 11px; }
  .sidebar-stats { display: none; }
  .admin-main { padding: 20px 16px; }
  .form-card { padding: 20px; }
  .posts-grid, .quotes-grid { grid-template-columns: 1fr; }
  .form-actions { flex-direction: column; }
}
</style>